import {
  InvalidTransactionIssue,
  InvalidTransactionResult,
  FinancialAlerts,
  SepaPaymentDTO,
  EvaluationResult,
  BatchSummary,
  HighRiskViolation,
  NonFATFViolation
} from '../types/validation';
import { extractRuleName } from '../utils/formatters';
import { CUSTOMER_COUNTRY_CODES, FATF_COUNTRIES, getIbanCountryCode, getCustomerCountryName, getCountryName, determineRiskLevel } from './countriesService';

const THRESHOLD_15K = 15000.00;


// Helper function to mask IBAN for privacy
function maskIban(iban: string): string {
  if (!iban || iban.length <= 8) {
    return iban;
  }

  const firstPart = iban.substring(0, 4);
  const lastPart = iban.substring(iban.length - 4);
  const maskedLength = iban.length - 8;
  const masked = '*'.repeat(maskedLength);

  return firstPart + masked + lastPart;
}

function createHighRiskAlertMessage(violation: HighRiskViolation): string {
  let message = `ALERT [${violation.riskLevel} RISK]: Transaction involves EU/FATF High-Risk Third Country. `;

  if (violation.debtorIsHighRisk && violation.creditorIsHighRisk) {
    message += `Both sender (${getCustomerCountryName(violation.debtorCountry)}) and receiver (${getCustomerCountryName(violation.creditorCountry)}) are from High-Risk countries. `;
  } else if (violation.debtorIsHighRisk) {
    message += `Sender is from High-Risk country: ${getCustomerCountryName(violation.debtorCountry)}. `;
  } else {
    message += `Receiver is from High-Risk country: ${getCustomerCountryName(violation.creditorCountry)}. `;
  }

  message += `Amount: EUR ${violation.amount.toFixed(2)}. `;
  message += "MANDATORY Enhanced Due Diligence (EDD) required. " +
    "Additional KYC verification, source of funds documentation, " +
    "and transaction monitoring必須 are mandatory per EU AML Directive.";

  return message;
}

function createNonFATFAlertMessage(violation: NonFATFViolation): string {
  let message = "Alert: Transaction involves Non-FATF country. ";

  if (violation.debtorIsNonFATF && violation.creditorIsNonFATF) {
    message += `Both sender (${getCountryName(violation.debtorCountry)}) and receiver (${getCountryName(violation.creditorCountry)}) are from Non-FATF countries. `;
  } else if (violation.debtorIsNonFATF) {
    message += `Sender is from Non-FATF country (${getCountryName(violation.debtorCountry)}). `;
  } else {
    message += `Receiver is from Non-FATF country (${getCountryName(violation.creditorCountry)}). `;
  }

  message += `Amount: EUR ${violation.amount.toFixed(2)}. Enhanced due diligence required.`;

  return message;
}

function createCustomerCountryAlertMessage(violation: NonFATFViolation, selectedCountries: string[]): string {
  const countryList = selectedCountries.map(c => getCountryName(c)).join(', ');
  let message = `Alert: Transaction involves country not in selected customer countries list (${countryList}). `;

  if (violation.debtorIsNonFATF && violation.creditorIsNonFATF) {
    message += `Both sender (${getCountryName(violation.debtorCountry)}) and receiver (${getCountryName(violation.creditorCountry)}) are from countries not in the selected list. `;
  } else if (violation.debtorIsNonFATF) {
    message += `Sender is from country not in selected list (${getCountryName(violation.debtorCountry)}). `;
  } else {
    message += `Receiver is from country not in selected list (${getCountryName(violation.creditorCountry)}). `;
  }

  message += `Amount: EUR ${violation.amount.toFixed(2)}. Enhanced due diligence required.`;

  return message;
}

// Alert evaluation methods
function evaluateHighRiskCountries(payments: SepaPaymentDTO[], results: EvaluationResult[]): void {
  if (!payments || payments.length === 0) {
    return;
  }

  const violations: HighRiskViolation[] = [];

  for (const payment of payments) {
    const debtorCountry = getIbanCountryCode(payment.debtorIban);
    const creditorCountry = getIbanCountryCode(payment.creditorIban);

    const debtorIsHighRisk = debtorCountry !== null && CUSTOMER_COUNTRY_CODES.has(debtorCountry);
    const creditorIsHighRisk = creditorCountry !== null && CUSTOMER_COUNTRY_CODES.has(creditorCountry);

    if (debtorIsHighRisk || creditorIsHighRisk) {
      violations.push({
        paymentId: payment.paymentId,
        debtorCountry,
        creditorCountry,
        debtorIsHighRisk,
        creditorIsHighRisk,
        amount: payment.amount,
        riskLevel: determineRiskLevel(debtorCountry, creditorCountry, payment.amount)
      });
    }
  }

  if (violations.length === 0) {
    return;
  }

  for (const violation of violations) {
    const alertMessage = createHighRiskAlertMessage(violation);

    for (const result of results) {
      if (result.paymentId === violation.paymentId) {
        const existingNotes = result.evaluationNotes;
        if (existingNotes && existingNotes.length > 0) {
          result.evaluationNotes = existingNotes + "; " + alertMessage;
        } else {
          result.evaluationNotes = alertMessage;
        }
        break;
      }
    }
  }
}

function evaluateNonFATFCountry(payments: SepaPaymentDTO[], results: EvaluationResult[]): void {
  if (!payments || payments.length === 0) {
    return;
  }

  const violations: NonFATFViolation[] = [];

  for (const payment of payments) {
    const debtorCountry = getIbanCountryCode(payment.debtorIban);
    const creditorCountry = getIbanCountryCode(payment.creditorIban);

    const debtorIsNonFATF = debtorCountry !== null && !FATF_COUNTRIES.has(debtorCountry);
    const creditorIsNonFATF = creditorCountry !== null && !FATF_COUNTRIES.has(creditorCountry);

    if (debtorIsNonFATF || creditorIsNonFATF) {
      violations.push({
        paymentId: payment.paymentId,
        debtorCountry,
        creditorCountry,
        debtorIsNonFATF,
        creditorIsNonFATF,
        amount: payment.amount
      });
    }
  }

  if (violations.length === 0) {
    return;
  }

  for (const violation of violations) {
    const alertMessage = createNonFATFAlertMessage(violation);

    for (const result of results) {
      if (result.paymentId === violation.paymentId) {
        const existingNotes = result.evaluationNotes;
        if (existingNotes && existingNotes.length > 0) {
          result.evaluationNotes = existingNotes + "; " + alertMessage;
        } else {
          result.evaluationNotes = alertMessage;
        }
        break;
      }
    }
  }
}

function evaluateCustomerCountryList(payments: SepaPaymentDTO[], results: EvaluationResult[], selectedCountries: string[]): void {
  if (!payments || payments.length === 0 || !selectedCountries || selectedCountries.length === 0) {
    return;
  }

  // Convert selected countries to Set for efficient lookup
  const selectedCountriesSet = new Set(selectedCountries.map(c => c.toUpperCase()));

  const violations: NonFATFViolation[] = [];

  for (const payment of payments) {
    const debtorCountry = getIbanCountryCode(payment.debtorIban);
    const creditorCountry = getIbanCountryCode(payment.creditorIban);

    // Check if country is NOT in the selected list
    const debtorNotInList = debtorCountry !== null && !selectedCountriesSet.has(debtorCountry);
    const creditorNotInList = creditorCountry !== null && !selectedCountriesSet.has(creditorCountry);

    if (debtorNotInList || creditorNotInList) {
      violations.push({
        paymentId: payment.paymentId,
        debtorCountry,
        creditorCountry,
        debtorIsNonFATF: debtorNotInList,
        creditorIsNonFATF: creditorNotInList,
        amount: payment.amount
      });
    }
  }

  if (violations.length === 0) {
    return;
  }

  for (const violation of violations) {
    const alertMessage = createCustomerCountryAlertMessage(violation, selectedCountries);

    for (const result of results) {
      if (result.paymentId === violation.paymentId) {
        const existingNotes = result.evaluationNotes;
        if (existingNotes && existingNotes.length > 0) {
          result.evaluationNotes = existingNotes + "; " + alertMessage;
        } else {
          result.evaluationNotes = alertMessage;
        }
        break;
      }
    }
  }
}

function evaluate3IncomingOneSenderToOneReciever(payments: SepaPaymentDTO[], results: EvaluationResult[]): void {
  if (!payments || payments.length === 0) {
    return;
  }

  // Group payments by sender-receiver pair
  const paymentsByPair = new Map<string, SepaPaymentDTO[]>();

  for (const payment of payments) {
    const pairKey = payment.debtorIban + "|" + payment.creditorIban;
    if (!paymentsByPair.has(pairKey)) {
      paymentsByPair.set(pairKey, []);
    }
    paymentsByPair.get(pairKey)!.push(payment);
  }

  const violations = new Map<string, string[]>();

  paymentsByPair.forEach((pairPayments, pairKey) => {
    if (pairPayments.length > 3) {
      const paymentIds = pairPayments.map((p: SepaPaymentDTO) => p.paymentId);
      violations.set(pairKey, paymentIds);
    }
  });

  if (violations.size === 0) {
    return;
  }

  violations.forEach((paymentIds, pairKey) => {
    const ibans = pairKey.split("|");
    const debtorIban = ibans[0];
    const creditorIban = ibans[1];

    const totalAmount = payments
      .filter(p => paymentIds.includes(p.paymentId))
      .reduce((sum, p) => sum + p.amount, 0);

    const averageAmount = totalAmount / paymentIds.length;

    const alertMessage = `Alert: More than 3 incoming payments detected - ${paymentIds.length} transactions from ${maskIban(debtorIban)} to ${maskIban(creditorIban)} in 1 day. ` +
      `Total amount: EUR ${totalAmount.toFixed(2)}, Average: EUR ${averageAmount.toFixed(2)} per transaction. ` +
      `Payment IDs: ${paymentIds.join(", ")}. Possible structuring or suspicious relationship.`;

    for (const result of results) {
      if (paymentIds.includes(result.paymentId)) {
        const existingNotes = result.evaluationNotes;
        if (existingNotes && existingNotes.length > 0) {
          result.evaluationNotes = existingNotes + "; " + alertMessage;
        } else {
          result.evaluationNotes = alertMessage;
        }
      }
    }
  });
}

function evaluate4OutgoingAbove15K(payments: SepaPaymentDTO[], results: EvaluationResult[]): void {
  const paymentsAbove15K = payments.filter(p => p.amount > THRESHOLD_15K);

  if (paymentsAbove15K.length === 0) {
    return;
  }

  // Group payments by debtor IBAN
  const paymentsByDebtor = new Map<string, SepaPaymentDTO[]>();
  for (const payment of paymentsAbove15K) {
    if (!paymentsByDebtor.has(payment.debtorIban)) {
      paymentsByDebtor.set(payment.debtorIban, []);
    }
    paymentsByDebtor.get(payment.debtorIban)!.push(payment);
  }

  const violatingPaymentIds = new Set<string>();
  const violationsByDebtor = new Map<string, string[]>();

  paymentsByDebtor.forEach((debtorPayments, debtorIban) => {
    if (debtorPayments.length > 4) {
      const paymentIds = debtorPayments.map((p: SepaPaymentDTO) => p.paymentId);
      paymentIds.forEach((id: string) => violatingPaymentIds.add(id));
      violationsByDebtor.set(debtorIban, paymentIds);
    } else if (debtorPayments.length >= 4) {
      // Check if they fall within 48 hours window (simplified implementation)
      const paymentIdsInWindow = checkPaymentsWithin48Hours(debtorPayments);
      if (paymentIdsInWindow.length > 4) {
        paymentIdsInWindow.forEach((id: string) => violatingPaymentIds.add(id));
        violationsByDebtor.set(debtorIban, paymentIdsInWindow);
      }
    }
  });

  if (violatingPaymentIds.size === 0) {
    return;
  }

  violationsByDebtor.forEach((paymentIds, debtorIban) => {
    const alertMessage = `Alert: More than 4 outgoing payments above EUR 15,000 detected from debtor ${maskIban(debtorIban)} within 48 hours. ` +
      `Total count: ${paymentIds.length}. Payment IDs: ${paymentIds.join(", ")}`;

    for (const result of results) {
      if (paymentIds.includes(result.paymentId)) {
        const existingNotes = result.evaluationNotes;
        if (existingNotes && existingNotes.length > 0) {
          result.evaluationNotes = existingNotes + "; " + alertMessage;
        } else {
          result.evaluationNotes = alertMessage;
        }
      }
    }
  });
}

function evaluateLinkedOperationsAboveCustomAmount(payments: SepaPaymentDTO[], results: EvaluationResult[], dynamicAmount: number, dynamicHours: number): void {
  const paymentsExceedingThreshold = payments.filter(p => p.amount > dynamicAmount);

  if (paymentsExceedingThreshold.length === 0) {
    return;
  }

  const count = paymentsExceedingThreshold.length;
  const transactionDetails = paymentsExceedingThreshold.map(p => p.paymentId).join(", ");

  const summaryMessage = `I found ${count} transaction${count > 1 ? "s" : ""} that ${count > 1 ? "have" : "has"} amount more than the threshold (${dynamicAmount}) within linked operations window (${dynamicHours} hours) and ${count > 1 ? "their" : "it's"} Id ${count > 1 ? "are" : "is"} ${transactionDetails}`;

  const exceedingPaymentIds = new Set(paymentsExceedingThreshold.map(p => p.paymentId));

  for (const result of results) {
    if (exceedingPaymentIds.has(result.paymentId)) {
      result.evaluationNotes = summaryMessage;
    }
  }
}

function checkPaymentsWithin48Hours(payments: SepaPaymentDTO[]): string[] {
  if (payments.length > 4) {
    return payments.map(p => p.paymentId);
  }

  return [];
}

// Main evaluation function
export function evaluateAlerts(payments: SepaPaymentDTO[], results: EvaluationResult[], alerts: FinancialAlerts): void {
  if (!alerts || !alerts.selectedAlerts || alerts.selectedAlerts.length === 0) {
    return;
  }

  for (const alert of alerts.selectedAlerts) {
    // Dynamic linked operations: "Transfers above {amount} EUR for linked operations in {hours} hours"
    if (alert.startsWith("Transfers above") && alert.includes("linked operations") && alert.includes("hours")) {
      // Extract amount and hours
      const amountMatch = alert.match(/Transfers above\s+(\d+[\d\s]*)\s*EUR/i);
      const hoursMatch = alert.match(/in\s+(\d+)\s*hours/i);
      const amountStr = amountMatch ? amountMatch[1].replace(/\s/g, '') : '50000';
      const dynamicAmount = Number(amountStr);
      const dynamicHours = hoursMatch ? Number(hoursMatch[1]) : 48;
      // For now reuse >50k evaluator but set notes on matching items
      // Here we simply mark payments exceeding dynamicAmount with a summary note
      evaluateLinkedOperationsAboveCustomAmount(payments, results, dynamicAmount, dynamicHours);
    } else if (alert.startsWith("Customer country list:")) {
      // Extract countries from alert text: "Customer country list: DE, FR, US"
      const countriesMatch = alert.match(/Customer country list:\s*(.+)/i);
      if (countriesMatch && countriesMatch[1]) {
        const countries = countriesMatch[1]
          .split(',')
          .map(c => c.trim().toUpperCase())
          .filter(c => c.length > 0);
        if (countries.length > 0) {
          evaluateCustomerCountryList(payments, results, countries);
        }
      } else if (alerts.selectedCountries && alerts.selectedCountries.length > 0) {
        // Fallback to selectedCountries from alerts object if available
        evaluateCustomerCountryList(payments, results, alerts.selectedCountries);
      }
    } else if (alert.includes("More than 4 outgoing above 15 000 EUR for 48 hours")) {
      evaluate4OutgoingAbove15K(payments, results);
    } else if (alert.includes("More than 3 incoming 1 sender to 1 receiver in 1 day")) {
      evaluate3IncomingOneSenderToOneReciever(payments, results);
    } else if (alert.includes("Non FATF country")) {
      evaluateNonFATFCountry(payments, results);
    } else if (alert.includes("High-risk third countries")) {
      evaluateHighRiskCountries(payments, results);
    }
  }
}

// Batch summary generation
export function generateBatchSummary(payments: SepaPaymentDTO[], results: EvaluationResult[]): BatchSummary {
  const summary: BatchSummary = {
    totalAmount: 0,
    paymentTypeCount: {},
    statusCount: {},
    countryCount: {},
    validationErrorCount: {}
  };

  // Calculate total amount
  summary.totalAmount = payments.reduce((sum, p) => sum + p.amount, 0);

  // Count payment types
  const paymentTypeCount: Record<string, number> = {};
  for (const payment of payments) {
    const type = payment.type || "SCT";
    paymentTypeCount[type] = (paymentTypeCount[type] || 0) + 1;
  }
  summary.paymentTypeCount = paymentTypeCount;

  // Count statuses from results
  const statusCount: Record<string, number> = {};
  for (const result of results) {
    const status = result.status.toString();
    statusCount[status] = (statusCount[status] || 0) + 1;
  }
  summary.statusCount = statusCount;

  // Count countries from debtor IBANs
  const countryCount: Record<string, number> = {};
  for (const payment of payments) {
    const country = getIbanCountryCode(payment.debtorIban);
    if (country) {
      countryCount[country] = (countryCount[country] || 0) + 1;
    }
  }
  summary.countryCount = countryCount;

  // Count validation errors
  const validationErrorCount: Record<string, number> = {};
  for (const result of results) {
    if (!result.isValid) {
      for (const message of result.validationMessages) {
        validationErrorCount[message] = (validationErrorCount[message] || 0) + 1;
      }
    }
  }
  summary.validationErrorCount = validationErrorCount;

  return summary;
}

// Convert parsed XML data to SepaPaymentDTO format
export function convertToSepaPayments(parsedData: any): SepaPaymentDTO[] {
  const payments: SepaPaymentDTO[] = [];
  
  if (!parsedData.paymentInformation || !parsedData.paymentInformation.transactions) {
    return payments;
  }

  const paymentInfo = parsedData.paymentInformation;
  const debtorIban = paymentInfo.debtorAccount?.iban || "";
  const debtorName = paymentInfo.debtor?.name || "";

  for (let i = 0; i < paymentInfo.transactions.length; i++) {
    const transaction = paymentInfo.transactions[i];
    const amount = parseFloat(transaction.amount || "0");
    
    payments.push({
      paymentId: transaction.endToEndId || `payment_${i}`,
      debtorIban,
      creditorIban: transaction.creditor?.iban || "",
      amount,
      currency: transaction.currency || "EUR",
      type: "SCT", // Default to SEPA Credit Transfer
      endToEndId: transaction.endToEndId,
      debtorName,
      creditorName: transaction.creditor?.name || "",
      remittanceInformation: transaction.remittanceInformation
    });
  }

  return payments;
}

// Local validation function that replaces backend calls
export function validateSepaPaymentsLocal(
  parsedData: any,
  alertsJson?: FinancialAlerts
): { results: InvalidTransactionResult[]; payments: SepaPaymentDTO[] } {
  // Convert parsed XML data to SepaPaymentDTO format
  const payments = convertToSepaPayments(parsedData);
  
  if (payments.length === 0) {
    return { results: [], payments: [] };
  }

  // Initialize evaluation results
  const results: EvaluationResult[] = payments.map((payment, index) => {
    const validationMessages: string[] = [];
    let isValid = true;
    let status: 'VALID' | 'INVALID' | 'SUSPICIOUS' = 'VALID';
    
    // Add some sample validation errors for demonstration
    if (payment.amount > 100000) {
      validationMessages.push('Amount exceeds maximum limit of EUR 100,000');
      isValid = false;
      status = 'INVALID';
    }
    
    if (!payment.debtorIban || payment.debtorIban.length < 15) {
      validationMessages.push('Invalid debtor IBAN format');
      isValid = false;
      status = 'INVALID';
    }
    
    if (!payment.creditorIban || payment.creditorIban.length < 15) {
      validationMessages.push('Invalid creditor IBAN format');
      isValid = false;
      status = 'INVALID';
    }
    
    return {
      paymentId: payment.paymentId,
      status: status,
      evaluationNotes: '',
      isValid: isValid,
      validationMessages: validationMessages
    };
  });

  // Apply alert evaluations if alerts are provided
  if (alertsJson && alertsJson.selectedAlerts && alertsJson.selectedAlerts.length > 0) {
    evaluateAlerts(payments, results, alertsJson);
  }

  // Convert EvaluationResult to InvalidTransactionResult format
  const invalidTransactionResults: InvalidTransactionResult[] = results.map((result, index) => {
    const payment = payments[index];
    const issues: InvalidTransactionIssue[] = [];
    
    // Add evaluation notes as issues if present
    if (result.evaluationNotes && result.evaluationNotes.trim().length > 0) {
      issues.push({
        code: 'ALERT',
        message: result.evaluationNotes
      });
    }

    // Add validation messages as issues
    for (const message of result.validationMessages) {
      issues.push({
        code: 'VALIDATION_ERROR',
        message: message,
        fieldPath: message.includes('debtor') ? 'debtorIban' : 
                  message.includes('creditor') ? 'creditorIban' :
                  message.includes('amount') ? 'amount' : undefined
      });
    }

    return {
      endToEndId: payment.endToEndId,
      paymentId: payment.paymentId || payment.endToEndId, // Include paymentId for easier matching
      index: index,
      issues: issues,
      evaluationNotes: result.evaluationNotes,
      status: result.status,
      isValid: result.isValid,
      validationMessages: result.validationMessages,
      // Include payment data directly in result for easier access
      payment: payment ? {
        amount: payment.amount,
        currency: payment.currency,
        debtorName: payment.debtorName,
        creditorName: payment.creditorName,
        debtorIban: payment.debtorIban,
        creditorIban: payment.creditorIban,
        type: payment.type
      } : undefined
    };
  });

  return { results: invalidTransactionResults, payments };
}

// Legacy function for backward compatibility - now uses local logic
export async function validateSepaJson(
  payload: unknown,
  alertsJson?: FinancialAlerts,
  options?: { endpoint?: string; signal?: AbortSignal }
): Promise<any> {
  // Use local validation instead of backend call
  try {
    const startTime = Date.now();
    const { results, payments } = validateSepaPaymentsLocal(payload, alertsJson);
    const endTime = Date.now();
    
    // Generate batch summary
    const evaluationResults: EvaluationResult[] = results.map(result => ({
      paymentId: result.endToEndId || `payment_${result.index}`,
      status: result.status || 'VALID',
      evaluationNotes: result.evaluationNotes || '',
      isValid: result.isValid !== false,
      validationMessages: result.validationMessages || []
    }));
    
    const summary = generateBatchSummary(payments, evaluationResults);
    
    // Count valid payments and payments with triggered alerts
    const validPayments = results.filter(r => r.isValid !== false).length;
    // Count payments with triggered alerts (not just invalid payments)
    const invalidPayments = results.filter(r => {
      const ruleTriggered = extractRuleName(r.validationMessages, r.issues);
      return ruleTriggered !== 'N/A';
    }).length;
    
    // Create batch evaluation response
    const batchResponse = {
      batchId: `batch_${Date.now()}`,
      totalPayments: results.length,
      validPayments: validPayments,
      invalidPayments: invalidPayments,
      processedAt: new Date().toISOString(),
      processingTimeMs: endTime - startTime,
      summary: summary,
      results: results,
      payments: payments // Include payments array for table component
    };
    
    console.log('Local validation batch response:', batchResponse);
    console.log('Payments array length:', payments.length);
    return batchResponse;
  } catch (error) {
    console.error('Local validation error:', error);
    throw new Error(`Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

